import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Star, Search, Filter, TrendingUp, TrendingDown, Award, AlertTriangle,
  User, Building2, Calendar, MessageSquare, Plus, Minus, CheckCircle,
  Loader2, ChevronRight, BarChart3, Calculator, DollarSign, Clock, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Using api from lib/axios

const RatingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  
  // Bonus/Malus modal
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [bonusValue, setBonusValue] = useState(0);
  const [bonusReason, setBonusReason] = useState('');
  const [savingBonus, setSavingBonus] = useState(false);
  
  // Financial bonus calculation modal
  const [showPrimeModal, setShowPrimeModal] = useState(false);
  const [calculatingPrime, setCalculatingPrime] = useState(false);
  const [primeResult, setPrimeResult] = useState(null);
  
  // View member bonuses modal
  const [showMemberBonusesModal, setShowMemberBonusesModal] = useState(false);
  const [memberBonuses, setMemberBonuses] = useState([]);
  const [loadingMemberBonuses, setLoadingMemberBonuses] = useState(false);

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    try {
      const response = await api.get('/ratings/');
      setRatings(response.data.ratings || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching ratings:', error);
      toast.error('Erreur lors du chargement des évaluations');
    } finally {
      setLoading(false);
    }
  };

  const openBonusModal = (rating) => {
    setSelectedRating(rating);
    setBonusValue(rating.bonus_malus || 0);
    setBonusReason(rating.bonus_malus_reason || '');
    setShowBonusModal(true);
  };

  const handleSaveBonus = async () => {
    if (!selectedRating) return;
    
    setSavingBonus(true);
    try {
      await axios.put(
        `${API}/ratings/${selectedRating.id}/bonus-malus`,
        { bonus_malus: bonusValue, reason: bonusReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bonus/malus appliqué avec succès');
      setShowBonusModal(false);
      fetchRatings();
    } catch (error) {
      console.error('Error saving bonus/malus:', error);
      toast.error('Erreur lors de l\'application du bonus/malus');
    } finally {
      setSavingBonus(false);
    }
  };

  const openPrimeCalculation = (rating) => {
    setSelectedRating(rating);
    setPrimeResult(null);
    setShowPrimeModal(true);
  };

  const handleCalculatePrimes = async () => {
    if (!selectedRating) return;
    
    setCalculatingPrime(true);
    try {
      const response = await axios.post(
        `${API}/ratings/${selectedRating.id}/calculate-member-bonuses`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrimeResult(response.data);
      toast.success(response.data.message);
      fetchRatings();
    } catch (error) {
      console.error('Error calculating primes:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du calcul des primes');
    } finally {
      setCalculatingPrime(false);
    }
  };

  const viewMemberBonuses = async (rating) => {
    setSelectedRating(rating);
    setLoadingMemberBonuses(true);
    setShowMemberBonusesModal(true);
    
    try {
      const response = await axios.get(
        `${API}/ratings/${rating.id}/member-bonuses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMemberBonuses(response.data);
    } catch (error) {
      console.error('Error fetching member bonuses:', error);
      toast.error('Erreur lors du chargement des primes');
      setMemberBonuses([]);
    } finally {
      setLoadingMemberBonuses(false);
    }
  };

  const getRatingColor = (rating) => {
    const total = rating + (selectedRating?.bonus_malus || 0);
    if (total >= 17) return 'text-emerald-600';
    if (total >= 13) return 'text-blue-600';
    if (total >= 10) return 'text-amber-600';
    return 'text-red-600';
  };

  const getRatingBadge = (rating) => {
    if (rating >= 17) return { label: 'Excellent', color: 'bg-emerald-100 text-emerald-700' };
    if (rating >= 13) return { label: 'Bien', color: 'bg-blue-100 text-blue-700' };
    if (rating >= 10) return { label: 'Moyen', color: 'bg-amber-100 text-amber-700' };
    return { label: 'À améliorer', color: 'bg-red-100 text-red-700' };
  };

  const filteredRatings = ratings.filter(r => {
    const matchesSearch = !searchQuery || 
      r.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.creative_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const totalRating = r.rating + (r.bonus_malus || 0);
    const matchesFilter = filterRating === 'all' ||
      (filterRating === 'excellent' && totalRating >= 17) ||
      (filterRating === 'good' && totalRating >= 13 && totalRating < 17) ||
      (filterRating === 'average' && totalRating >= 10 && totalRating < 13) ||
      (filterRating === 'poor' && totalRating < 10);
    
    return matchesSearch && matchesFilter;
  });

  const renderStars = (rating, size = 'w-4 h-4') => {
    const fullStars = Math.floor(rating / 4); // Convert 20-scale to 5-star
    const hasHalf = (rating % 4) >= 2;
    
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={cn(
              size,
              i <= fullStars ? 'fill-amber-400 text-amber-400' :
              i === fullStars + 1 && hasHalf ? 'fill-amber-200 text-amber-400' :
              'text-slate-300'
            )}
          />
        ))}
      </div>
    );
  };

  const renderRatingDetail = (detail) => {
    if (!detail) return null;
    
    const criteria = [
      { key: 'ecoute', label: 'Écoute', icon: '👂' },
      { key: 'creativite', label: 'Créativité', icon: '🎨' },
      { key: 'delai', label: 'Délai', icon: '⏱️' },
      { key: 'budget', label: 'Budget', icon: '💰' }
    ];
    
    return (
      <div className="grid grid-cols-4 gap-2 mt-2">
        {criteria.map(c => (
          <div key={c.key} className="text-center p-2 bg-slate-50 rounded-lg">
            <span className="text-sm">{c.icon}</span>
            <p className="text-xs text-slate-500">{c.label}</p>
            <p className="font-semibold text-sm">{detail[c.key] || 0}/5</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8 pt-32">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Évaluations des projets
              </h1>
              <p className="text-slate-500 mt-1">
                Consultez les évaluations clients et attribuez des bonus/malus
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.total || 0}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Star className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.average_rating || 0}/20</p>
                    <p className="text-xs text-slate-500">Moyenne</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Award className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.excellent || 0}</p>
                    <p className="text-xs text-slate-500">Excellents</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.good || 0}</p>
                    <p className="text-xs text-slate-500">Bons</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.poor || 0}</p>
                    <p className="text-xs text-slate-500">À améliorer</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Rechercher par projet, client ou créatif..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterRating} onValueChange={setFilterRating}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filtrer par note" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les notes</SelectItem>
                    <SelectItem value="excellent">Excellent (≥17)</SelectItem>
                    <SelectItem value="good">Bien (13-16)</SelectItem>
                    <SelectItem value="average">Moyen (10-12)</SelectItem>
                    <SelectItem value="poor">À améliorer (&lt;10)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Ratings List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredRatings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Star className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucune évaluation</h3>
                <p className="text-slate-500">Les évaluations des projets apparaîtront ici</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredRatings.map((rating) => {
                const totalRating = rating.rating + (rating.bonus_malus || 0);
                const badge = getRatingBadge(totalRating);
                
                return (
                  <Card key={rating.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Left: Project & Rating Info */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-semibold text-lg text-slate-800">{rating.project_name}</h3>
                              <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-4 h-4" />
                                  {rating.client_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {rating.creative_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(rating.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                            </div>
                            <Badge className={badge.color}>{badge.label}</Badge>
                          </div>
                          
                          {/* Rating Detail */}
                          {rating.ratings_detail && renderRatingDetail(rating.ratings_detail)}
                          
                          {/* Comment */}
                          {rating.comment && (
                            <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                              <p className="text-sm text-slate-600 flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" />
                                &ldquo;{rating.comment}&rdquo;
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Right: Score & Actions */}
                        <div className="flex lg:flex-col items-center gap-4 lg:border-l lg:pl-6">
                          <div className="text-center">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={cn("text-3xl font-bold", getRatingColor(totalRating))}>
                                {totalRating}
                              </span>
                              <span className="text-lg text-slate-400">/20</span>
                            </div>
                            {renderStars(totalRating)}
                            
                            {/* Bonus/Malus indicator */}
                            {rating.bonus_malus !== 0 && rating.bonus_malus !== undefined && (
                              <div className={cn(
                                "mt-2 px-2 py-1 rounded-full text-xs font-medium",
                                rating.bonus_malus > 0 
                                  ? "bg-emerald-100 text-emerald-700" 
                                  : "bg-red-100 text-red-700"
                              )}>
                                {rating.bonus_malus > 0 ? '+' : ''}{rating.bonus_malus} pts
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openBonusModal(rating)}
                              className="gap-1"
                            >
                              {rating.bonus_malus ? (
                                <>Modifier</>
                              ) : (
                                <>
                                  <Plus className="w-3 h-3" />
                                  <Minus className="w-3 h-3" />
                                  Bonus/Malus
                                </>
                              )}
                            </Button>
                            
                            {/* Button to calculate financial primes */}
                            {rating.member_bonuses_calculated ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewMemberBonuses(rating)}
                                className="gap-1 text-emerald-600"
                              >
                                <Eye className="w-3 h-3" />
                                Voir primes
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPrimeCalculation(rating)}
                                className="gap-1 text-blue-600"
                              >
                                <Calculator className="w-3 h-3" />
                                Calculer primes
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {/* Bonus/Malus Modal */}
      <Dialog open={showBonusModal} onOpenChange={setShowBonusModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Attribuer un Bonus/Malus
            </DialogTitle>
            <DialogDescription>
              Ajoutez ou retirez des points à cette évaluation
            </DialogDescription>
          </DialogHeader>
          
          {selectedRating && (
            <div className="space-y-6 py-4">
              {/* Project Info */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-semibold">{selectedRating.project_name}</p>
                <p className="text-sm text-slate-500">
                  Note initiale: {selectedRating.rating}/20
                </p>
              </div>
              
              {/* Bonus/Malus Slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Bonus/Malus</Label>
                  <span className={cn(
                    "text-xl font-bold",
                    bonusValue > 0 ? "text-emerald-600" : bonusValue < 0 ? "text-red-600" : "text-slate-600"
                  )}>
                    {bonusValue > 0 ? '+' : ''}{bonusValue} pts
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBonusValue(Math.max(-5, bonusValue - 1))}
                    disabled={bonusValue <= -5}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  
                  <div className="flex-1 h-2 bg-slate-200 rounded-full relative">
                    <div 
                      className={cn(
                        "absolute top-0 h-full rounded-full transition-all",
                        bonusValue >= 0 ? "bg-emerald-500 left-1/2" : "bg-red-500 right-1/2"
                      )}
                      style={{ 
                        width: `${Math.abs(bonusValue) * 10}%`,
                        ...(bonusValue < 0 ? { left: 'auto' } : { right: 'auto' })
                      }}
                    />
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-slate-300 rounded-full" />
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBonusValue(Math.min(5, bonusValue + 1))}
                    disabled={bonusValue >= 5}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex justify-between text-xs text-slate-500">
                  <span>-5 (Malus max)</span>
                  <span>0</span>
                  <span>+5 (Bonus max)</span>
                </div>
              </div>
              
              {/* Final Score Preview */}
              <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl text-center">
                <p className="text-sm text-slate-600 mb-1">Note finale</p>
                <p className={cn(
                  "text-3xl font-bold",
                  getRatingColor(selectedRating.rating + bonusValue)
                )}>
                  {selectedRating.rating + bonusValue}/20
                </p>
              </div>
              
              {/* Reason */}
              <div className="space-y-2">
                <Label>Raison (optionnel)</Label>
                <Textarea
                  value={bonusReason}
                  onChange={(e) => setBonusReason(e.target.value)}
                  placeholder="Expliquez pourquoi vous attribuez ce bonus/malus..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBonusModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveBonus}
              disabled={savingBonus}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {savingBonus ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prime Calculation Modal */}
      <Dialog open={showPrimeModal} onOpenChange={setShowPrimeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Calculer les primes financières
            </DialogTitle>
            <DialogDescription>
              Calculez les bonus ou malus financiers pour les membres de ce projet
            </DialogDescription>
          </DialogHeader>
          
          {selectedRating && (
            <div className="space-y-4 py-4">
              {/* Project Info */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-semibold">{selectedRating.project_name}</p>
                <p className="text-sm text-slate-500">
                  Note finale: {selectedRating.rating + (selectedRating.bonus_malus || 0)}/20
                </p>
              </div>
              
              {/* Rules explanation */}
              <div className="space-y-2 text-sm">
                <h4 className="font-medium text-slate-700">Règles de calcul :</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 p-2 bg-emerald-50 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-emerald-700">Note ≥ 18/20 :</span>
                      <span className="text-emerald-600 ml-1">+10% bonus sur le temps passé</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-red-50 rounded-lg">
                    <TrendingDown className="w-4 h-4 text-red-600 mt-0.5" />
                    <div>
                      <span className="font-medium text-red-700">Note &lt; 13/20 + retard :</span>
                      <span className="text-red-600 ml-1">-10% malus sur le temps passé</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 p-2 bg-slate-100 rounded-lg">
                    <span className="w-4 h-4 text-slate-600">—</span>
                    <div>
                      <span className="font-medium text-slate-700">Note 13-17.99/20 :</span>
                      <span className="text-slate-600 ml-1">Aucun ajustement</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Result if calculated */}
              {primeResult && (
                <div className="mt-4 p-4 border rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Type d&apos;ajustement :</span>
                    <Badge className={primeResult.bonus_type === 'bonus' ? 'bg-emerald-100 text-emerald-700' : primeResult.bonus_type === 'malus' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>
                      {primeResult.bonus_type === 'bonus' ? 'Bonus +10%' : primeResult.bonus_type === 'malus' ? 'Malus -10%' : 'Aucun'}
                    </Badge>
                  </div>
                  
                  {primeResult.members && primeResult.members.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Membres concernés :</p>
                      {primeResult.members.map((member, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{member.member_name}</p>
                            <p className="text-xs text-slate-500">{member.total_hours}h travaillées</p>
                          </div>
                          <div className="text-right">
                            <p className={cn(
                              "font-bold",
                              member.bonus_amount > 0 ? "text-emerald-600" : member.bonus_amount < 0 ? "text-red-600" : "text-slate-600"
                            )}>
                              {member.bonus_amount > 0 ? '+' : ''}{member.bonus_amount.toFixed(2)}€
                            </p>
                            <p className="text-xs text-slate-500">Final: {member.final_amount.toFixed(2)}€</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-2">
                      Aucun membre éligible ou pas de temps enregistré
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPrimeModal(false)}>
              Fermer
            </Button>
            {!primeResult && (
              <Button
                onClick={handleCalculatePrimes}
                disabled={calculatingPrime}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {calculatingPrime ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Calculator className="w-4 h-4 mr-2" />
                )}
                Calculer les primes
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Member Bonuses Modal */}
      <Dialog open={showMemberBonusesModal} onOpenChange={setShowMemberBonusesModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Primes calculées
            </DialogTitle>
            <DialogDescription>
              Détail des primes attribuées pour ce projet
            </DialogDescription>
          </DialogHeader>
          
          {selectedRating && (
            <div className="space-y-4 py-4">
              {/* Project Info */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-semibold">{selectedRating.project_name}</p>
                <p className="text-sm text-slate-500">
                  Note: {selectedRating.rating + (selectedRating.bonus_malus || 0)}/20 • 
                  Type: {selectedRating.bonus_type === 'bonus' ? 'Bonus +10%' : selectedRating.bonus_type === 'malus' ? 'Malus -10%' : 'Neutre'}
                </p>
              </div>
              
              {/* Member bonuses list */}
              {loadingMemberBonuses ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : memberBonuses.length > 0 ? (
                <div className="space-y-3">
                  {memberBonuses.map((bonus, idx) => (
                    <div key={idx} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <span className="font-medium">{bonus.member_name}</span>
                        </div>
                        <Badge className={bonus.bonus_type === 'bonus' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {bonus.bonus_type === 'bonus' ? 'Bonus' : 'Malus'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <p className="text-slate-500">Heures</p>
                          <p className="font-medium">{bonus.total_hours}h</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Valeur base</p>
                          <p className="font-medium">{bonus.total_value?.toFixed(2)}€</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Ajustement</p>
                          <p className={cn(
                            "font-bold",
                            bonus.bonus_amount > 0 ? "text-emerald-600" : "text-red-600"
                          )}>
                            {bonus.bonus_amount > 0 ? '+' : ''}{bonus.bonus_amount?.toFixed(2)}€
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t flex justify-between items-center">
                        <span className="text-sm text-slate-500">Montant final</span>
                        <span className="font-bold text-lg">{bonus.final_amount?.toFixed(2)}€</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Aucune prime calculée pour ce projet</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMemberBonusesModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RatingsPage;
